import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import { motion, AnimatePresence } from 'framer-motion';
import { FiPackage, FiUser, FiClock, FiMapPin, FiMail, FiCreditCard } from 'react-icons/fi';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

export default function Activity() {
  const [unseenOrders, setUnseenOrders] = useState([]);
  const [newUsers, setNewUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    async function fetchOrderData() {
      try {
        const adminToken = Cookies.get('adminKey');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URI}admin/unseenOrders`, {
          method: 'GET',
          headers: {
            "Admin-Token": adminToken
          }
        });
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        const data = await response.json();
        setUnseenOrders(data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    }

    async function fetchNewUsers() {
      try {
        const adminToken = Cookies.get('adminKey');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URI}admin/newUsers`, {
          method: 'GET',
          headers: {
            "Admin-Token": adminToken
          }
        });
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        const data = await response.json();
        setNewUsers(data);
      } catch (error) {
        console.error("Error fetching new users:", error);
      }
    }

    fetchOrderData();
    fetchNewUsers();
  }, []);

  const handleMarkAsSeen = async (orderId) => {
    try {
      const adminToken = Cookies.get('adminKey');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URI}admin/markOrderAsSeen`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          "Admin-Token": adminToken
        },
        body: JSON.stringify({ orderId }),
      });
      if (!response.ok) {
        throw new Error('Failed to mark order as seen');
      }
      // Remove the order from the unseen orders list
      setUnseenOrders((prev) => prev.filter(order => order.id !== orderId));
      setSelectedOrder(null); // Close the popup
    } catch (error) {
      console.error('Error marking order as seen:', error);
    }
  };

  const formatDate = (date) => {
    return format(new Date(date), "d MMMM yyyy 'alle' HH:mm", { locale: it });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#406580]"></div>
      </div>
    );
  }

  return (
    <section className="w-full px-6 py-8">
      <div className="max-w-[100rem] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Orders Section */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center space-x-2 mb-6">
              <FiPackage className="text-2xl text-[#406580]" />
              <h2 className="text-xl font-bold text-gray-800">Ordini Non Letti</h2>
              <span className="bg-[#406580] text-white text-sm px-2 py-1 rounded-full">
                {unseenOrders.length}
              </span>
            </div>

            <div className="space-y-4 max-h-[600px] overflow-y-auto pr-4">
              {unseenOrders.length > 0 ? (
                unseenOrders.map((order) => (
                  <motion.div
                    key={order.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="group bg-gray-50 hover:bg-white rounded-lg p-4 border border-gray-200 transition-all duration-200 hover:shadow-md"
                  >
                    <div className="flex justify-between items-start">
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <span className="text-lg font-semibold text-gray-800">#{order.id}</span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            order.status === "pending" ? "bg-yellow-100 text-yellow-800" : 
                            order.status === "paid" ? "bg-green-100 text-green-800" : 
                            "bg-blue-100 text-blue-800"
                          }`}>
                            {order.status}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2 text-sm text-gray-600">
                          <FiClock className="text-gray-400" />
                          <span>{formatDate(order.orderCreationTime)}</span>
                        </div>
                        <div className="flex items-center space-x-2 text-sm text-gray-600">
                          <FiCreditCard className="text-gray-400" />
                          <span>€{order.paymentTotal.toFixed(2)}</span>
                        </div>
                      </div>
                      <button
                        onClick={() => setSelectedOrder(order)}
                        className="bg-[#406580] text-white px-4 py-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-sm font-medium hover:bg-[#305570]"
                      >
                        Dettagli
                      </button>
                    </div>
                  </motion.div>
                ))
              ) : (
                <div className="text-center py-12">
                  <FiPackage className="mx-auto text-4xl text-gray-400 mb-4" />
                  <p className="text-gray-500">Non ci sono nuovi ordini da visualizzare</p>
                </div>
              )}
            </div>
          </div>

          {/* Users Section */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center space-x-2 mb-6">
              <FiUser className="text-2xl text-[#406580]" />
              <h2 className="text-xl font-bold text-gray-800">Nuovi Utenti</h2>
              <span className="bg-[#406580] text-white text-sm px-2 py-1 rounded-full">
                {newUsers.length}
              </span>
            </div>

            <div className="space-y-4 max-h-[600px] overflow-y-auto pr-4">
              {newUsers.length > 0 ? (
                newUsers.map((user) => (
                  <motion.div
                    key={user.email}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:shadow-md transition-all duration-200"
                  >
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-gray-800">
                          {user.firstName} {user.lastName}
                        </h3>
                        <span className="text-xs text-gray-500">
                          {formatDate(user.createdAt)}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="flex items-center space-x-2 text-gray-600">
                          <FiMail className="text-gray-400" />
                          <span>{user.email}</span>
                        </div>
                        <div className="flex items-center space-x-2 text-gray-600">
                          <FiMapPin className="text-gray-400" />
                          <span>{user.city}, {user.country}</span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))
              ) : (
                <div className="text-center py-12">
                  <FiUser className="mx-auto text-4xl text-gray-400 mb-4" />
                  <p className="text-gray-500">Non ci sono nuovi utenti da visualizzare</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Order Details Modal */}
        <AnimatePresence>
          {selectedOrder && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-white rounded-xl shadow-xl max-w-lg w-full overflow-hidden"
              >
                <div className="p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-bold text-gray-800">Dettagli Ordine #{selectedOrder.id}</h3>
                    <button
                      onClick={() => setSelectedOrder(null)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      ✕
                    </button>
                  </div>
                  
                  <div className="space-y-4">
                    {/* Order details content */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <p className="text-sm text-gray-500">Stato</p>
                        <p className="font-medium text-gray-800">{selectedOrder.status}</p>
                      </div>
                      <div className="space-y-2">
                        <p className="text-sm text-gray-500">Totale</p>
                        <p className="font-medium text-gray-800">€{selectedOrder.paymentTotal}</p>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <p className="text-sm text-gray-500">Indirizzo di Spedizione</p>
                      <p className="font-medium text-gray-800">{selectedOrder.shippingAddress}</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3">
                  <button
                    onClick={() => setSelectedOrder(null)}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Chiudi
                  </button>
                  <button
                    onClick={() => handleMarkAsSeen(selectedOrder.id)}
                    className="px-4 py-2 text-white bg-[#406580] rounded-lg hover:bg-[#305570] transition-colors"
                  >
                    Segna come Letto
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
